
import React, { useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import {
    Users,
    Target,
    Smartphone,
    Shield,
    TrendingUp,
    CheckCircle,
    CheckCircle2,
    Globe,
    Server,
    ShieldCheck
} from 'lucide-react';
import OurCompanyImage from "../../assets/OurCompany.webp"
import visionImage from "../../assets/vision.webp"
import vision4Image from "../../assets/vision4.webp"
import aboutusimage from "../../assets/aboutus.avif"

const Icons = {
    Home: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
    ),
    Check: () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F46653" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    )
};

const AboutPage = () => {
    const { scrollYProgress } = useScroll();
    const [isGray, setIsGray] = useState(false);

    // Hero Section Parallax (Original)
    const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.05]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0.3]);
    const springScale = useSpring(heroScale, { stiffness: 100, damping: 30 });

    const handleHomeClick = () => {
        window.location.href = "/";
    };

    // --- STYLE FOR SECTION 2 BACKGROUND ---
    const bgPuzzleStyle = {
        backgroundImage: `
      linear-gradient(#0C4550 1px, transparent 1px),
      linear-gradient(90deg, #0C4550 1px, transparent 1px)
    `,
        backgroundSize: '25px 25px',
    };

    // Vision Points Data
    const allVisionPoints = [
        "To become the most trusted bulk SMS communication platform for enterprises worldwide.",
        "To deliver lightning-fast, reliable, and scalable messaging infrastructure without compromise.",
        "To empower businesses with secure A2P messaging that strengthens customer engagement.",
        "To continuously innovate routing intelligence for maximum delivery and lowest latency.",
        "To set global benchmarks in compliance, transparency, and data security.",
        "To simplify complex telecom systems into intuitive, developer-friendly solutions.",
        "To support business growth by ensuring messages reach customers instantly, every time.",
        "To build long-term partnerships through service excellence and operational reliability."
    ];

    // Split points: First 4 and Remaining 4
    const firstFourPoints = allVisionPoints.slice(0, 4);
    const lastFourPoints = allVisionPoints.slice(4);

    return (
        <div className="min-h-screen bg-white font-sans selection:bg-[#F46653] selection:text-white">

            {/* SECTION 1: HERO - UNTOUCHED */}
            <section className="relative h-[87vh] w-full flex items-center justify-center overflow-hidden bg-[#0C4550]">
                <motion.div
                    style={{ scale: springScale, opacity: heroOpacity }}
                    className="absolute inset-0 z-0"
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-[#0C4550]/20 to-[#0C4550]/60 z-10"></div>
                    <img
                        src={aboutusimage}
                        alt="Global Digital Communication Network"
                        className="w-full h-full object-cover"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-6 max-w-6xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <button
                            onClick={handleHomeClick}
                            className="inline-flex items-center gap-3 px-4 py-2 bg-[#F46653]/20 border border-[#F46653]/30 rounded-lg mb-8 backdrop-blur-md hover:bg-[#F46653]/40 transition-all cursor-pointer group"
                        >
                            <Icons.Home />
                            <span className="text-white font-black text-sm uppercase tracking-[0.3em] group-hover:text-gray-100 transition-colors">
                                Home
                            </span>
                        </button>

                        <div className="overflow-hidden">
                            <motion.h1
                                initial={{ x: "-100%", opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{
                                    duration: 1.2,
                                    delay: 0.5,
                                    ease: [0.16, 1, 0.3, 1]
                                }}
                                className="text-6xl md:text-9xl font-black text-white mb-8 tracking-tighter leading-none drop-shadow-lg"
                            >
                                ABOUT <span className="text-[#F46653]">US</span>
                            </motion.h1>
                        </div>

                        <div className="w-24 h-1.5 bg-[#F46653] mx-auto rounded-full mb-10 shadow-[0_0_20px_rgba(244,102,83,0.5)]"></div>

                        <p className="text-lg md:text-2xl text-gray-100 font-medium max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                            Empowering global businesses with ultra-fast, reliable, and scalable Bulk SMS solutions.
                            We bridge the gap between you and your customers instantly.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* SECTION 2: WHO WE ARE */}
            <section className="relative w-full py-24 overflow-hidden bg-white">
                <div className="absolute inset-0 z-0 pointer-events-none">
                    <div
                        className="absolute inset-0 opacity-[0.06]"
                        style={bgPuzzleStyle}
                    ></div>
                </div>

                <div className="max-w-7xl mx-auto px-6 lg:px-20 relative z-10">
                    <div className="flex flex-col lg:flex-row items-start gap-16">

                        {/* LEFT SIDE: CONTENT */}
                        <motion.div
                            initial={{ opacity: 0, x: -100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="lg:w-1/2 space-y-8"
                        >
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 text-[#F46653] font-bold uppercase tracking-widest text-sm">
                                    <div className="w-12 h-[3px] bg-[#0C4550]"></div>
                                    ABOUT
                                </div>
                                <p className="text-4xl md:text-5xl font-black text-gray-800 leading-tight ">
                                    Our Company
                                </p>
                            </div>

                            <div className="space-y-6 text-[17px] text-gray-700 leading-8 text-justify">
                                <p>
                                    Founded in <span className="font-bold text-[#0C4550]">2013</span> with headquarters in <span className="font-bold text-[#0C4550]">Noida</span>, Eurofox Bulk SMS Service has evolved from a visionary startup into a global telecommunications powerhouse. We established this platform with a singular mission: to simplify complex communication infrastructure for modern enterprises. Today, we stand as a premier provider of A2P messaging, bridging the gap between businesses and millions of customers worldwide.
                                </p>
                                <p>
                                    We specialize in high-throughput messaging architecture designed for scale. Our platform is engineered to handle critical OTPs, transactional alerts, and promotional campaigns with industry-leading latency. By leveraging direct carrier connections and intelligent routing algorithms, we ensure that your messages are delivered instantly and securely, adhering to strict DLT compliance and global regulatory standards.
                                </p>
                                <p>
                                    At Eurofox, we believe that communication is the backbone of business growth. Our team of expert engineers and support specialists work around the clock to maintain 99.9% uptime, ensuring your business never stops moving. We are not just a service provider; we are your strategic partner in building lasting customer relationships through the power of instant connection.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-4">
                                {[
                                    { icon: Globe, label: "Global Reach" },
                                    { icon: Server, label: "24/7 Support" },
                                    { icon: ShieldCheck, label: "Secure Routes" },
                                    { icon: CheckCircle2, label: "Direct Carrier" }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 text-gray-800 font-semibold">
                                        <item.icon className="w-5 h-5 text-[#0C4550]" />
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* RIGHT SIDE: IMAGE AND STATS */}
                        <motion.div
                            initial={{ opacity: 0, x: 100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                            className="lg:w-1/2 w-full"
                        >
                            <div className="relative group">
                                <div className="absolute -inset-4 bg-gradient-to-tr from-[#0C4550]/10 to-[#F46653]/10 rounded-3xl -z-10 blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                                <div className="relative w-full aspect-[4/5] md:aspect-square lg:aspect-[4/5] bg-slate-200 rounded-3xl overflow-hidden shadow-2xl border-[10px] border-white flex flex-col items-center justify-center text-slate-400">
                                    <img
                                        src={OurCompanyImage}
                                        alt="Office Placeholder"
                                        onClick={() => setIsGray(!isGray)}
                                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out cursor-pointer hover:scale-105 ${isGray ? 'grayscale' : ''}`}
                                    />
                                </div>
                            </div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.6, duration: 0.6 }}
                                className="flex items-center justify-end gap-5 mt-16 md:mr-4"
                            >
                                <span className="text-8xl md:text-9xl font-black text-[#0C4550] leading-none tracking-tighter">
                                    10
                                </span>
                                <div className="text-lg md:text-xl font-bold text-gray-800 leading-tight">
                                    Years <br />
                                    experience <br />
                                    working.
                                </div>
                                <div className="h-20 w-1.5 bg-[#F46653] rounded-full"></div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* SECTION 3: OUR VISION - UPDATED STRUCTURE */}
            <section className="relative w-full py-28 overflow-hidden bg-[#0C4550]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(244,102,83,0.15),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.06),transparent_40%)]"></div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-20">
                    <div className="flex flex-col lg:flex-row items-start gap-16">

                        {/* LEFT COLUMN: Image 1 + Last 4 Vision Points */}
                        <motion.div
                            initial={{ opacity: 0, x: -100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.9, ease: "easeOut" }}
                            className="lg:w-1/2 w-full flex flex-col gap-10"
                        >
                            {/* Image 1 */}
                            <div className="relative w-full rounded-3xl overflow-hidden bg-white/10 backdrop-blur-xl shadow-xl aspect-video md:aspect-[4/3]">
                                <img
                                    src={visionImage}
                                    alt="Vision 1"
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            {/* The Remaining 4 Vision Points (Below Image 1) */}
                            <div className="space-y-6 text-[17px] text-gray-200 leading-relaxed">
                                {lastFourPoints.map((text, idx) => (
                                    <div key={idx} className="flex items-start gap-4">
                                        <span className="mt-1 w-2.5 h-2.5 rounded-full bg-[#F46653] flex-shrink-0"></span>
                                        <p>{text}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        {/* RIGHT COLUMN: Heading + First 4 Points + Image 2 */}
                        <motion.div
                            initial={{ opacity: 0, x: 100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
                            className="lg:w-1/2 w-full flex flex-col gap-10"
                        >
                            {/* Heading & First 4 Points */}
                            <div className="space-y-10">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 text-[#F46653] font-bold uppercase tracking-widest text-sm">
                                        <div className="w-14 h-[3px] bg-[#F46653]"></div>
                                        Our Vision
                                    </div>
                                    <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
                                        Shaping the Future of <br />
                                        <span className="text-[#F46653]">Global Messaging</span>
                                    </h2>
                                </div>

                                <div className="space-y-6 text-[17px] text-gray-200 leading-relaxed">
                                    {firstFourPoints.map((text, idx) => (
                                        <div key={idx} className="flex items-start gap-4">
                                            <span className="mt-1 w-2.5 h-2.5 rounded-full bg-[#F46653] flex-shrink-0"></span>
                                            <p>{text}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Image 2 (Moved below the first 4 points) */}
                            <div className="relative w-full rounded-3xl overflow-hidden backdrop-blur-xl shadow-xl aspect-video md:aspect-[4/3] mt-2">
                                <img
                                    src={vision4Image}
                                    alt="Vision 2"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>

        </div>
    );
};

export default AboutPage;